import React, { useCallback, useState } from "react"

import type { SegmentTrackProductClickedEvent } from "@beautypie/segment/Track/ProductClicked"

import { useLocale, useUserV2 } from "hooks"
import { useVariantIds } from "hooks/useVariantIds"
import {
  addBasketItemsWithSpinner,
  addWishlistItemBySKU,
  removeWishlistItemBySKU,
} from "lib/api"
import { Logger } from "lib/logger"
import {
  ProductCard,
  ProductCardBadge,
  ProductCardBrandName,
  ProductCardButton,
  ProductCardColorSwatch,
  ProductCardImage,
  ProductCardProductName,
  ProductCardRating,
  ProductCardRibbon,
  ProductCardVariationName,
  ProductCardWishlist,
} from "packages/core-shopping/components/ProductCard"
import {
  ShadeSelectorHeader,
  ShadeSelectorModal,
  ShadeSelectorModalContentPLP,
  ShadeSelectorModalFooter,
} from "packages/core-shopping/components/ShadeSelectorModal"
import { Button } from "packages/ui-shared/components/Button/Button"
import { useSegment } from "tracking"
import type {
  MultipleProductCard,
  MultipleProductCardVariant,
  SingularProductCard,
  VariantType,
} from "types"
import { navigateTo } from "utils/navigateTo"
import { navigate } from "utils/routes"

import type { PurchaseType } from "components/Shop/PDPPage/types"

import { SecondaryProductCardButton } from "packages/core-shopping/components/ProductCard/SecondaryProductCardButton"
import { isMultipleProductCard } from "packages/core-shopping/shared/isMultipleProductCard"
import { toProductCardData } from "packages/core-shopping/shared/toProductCardData"
import { toVariantType } from "packages/core-shopping/shared/toVariantType"
import { link, productCardFooter } from "./ProductCardFactory.css"
import type { AddBasketItemType } from "lib/api/addBasketItems"

type ResultType = SegmentTrackProductClickedEvent["properties"]["result_type"]
type Props = {
  productCard: MultipleProductCard | SingularProductCard
  setConfirmation?: React.Dispatch<React.SetStateAction<boolean>>
  setConfirmationVariant?: React.Dispatch<
    React.SetStateAction<VariantType | null>
  >
  confirmationVariant?: VariantType | null
  pdpPurchaseType?: PurchaseType
  resultType?: SegmentTrackProductClickedEvent["properties"]["result_type"]
  handleOnSecondaryButtonClicked?: (sku: string) => void
  cardPosition?: number
  numResultsPerPage?: number
  resultCount?: number
  resultId?: string
  useCloudinary?: boolean
}

const giftMembershipIdstoSkus = {
  "3074457345616768734": "BP_PLUS_GIFT_UK",
  "3074457345616760245": "BP_PLUS_GIFT_US",
}

/* istanbul ignore next */
export const ProductCardFactory: React.FC<Props> = ({
  productCard,
  setConfirmation,
  setConfirmationVariant,
  confirmationVariant = null,
  resultType,
  handleOnSecondaryButtonClicked,
  cardPosition,
  numResultsPerPage,
  resultCount,
  resultId,
  useCloudinary = false,
}) => {
  const { wishlist, isLoggedIn } = useUserV2()
  const { track } = useSegment()

  const inWishlist = wishlist?.some(
    (item) => item.variant?.sku === productCard.variants[0].sku
  )

  const { giftMembershipProductId } = useVariantIds()
  const giftMembershipSku = giftMembershipIdstoSkus[giftMembershipProductId]
  const isGiftMembership = giftMembershipSku === productCard.variants[0].sku

  const locale = useLocale()

  const [activeShade, setActiveShade] =
    useState<MultipleProductCardVariant | null>(
      isMultipleProductCard(productCard) ? productCard.variants[0] : null
    )

  const [isShadeSelectorOpen, setIsShadeSelectorOpen] = useState(false)
  const [shadeButtonClicked, setShadeButtonClicked] = useState(false)

  const position = activeShade
    ? productCard.variants.findIndex(
        (variant) => variant.sku === activeShade?.sku
      )
    : 0
  const productCardData = {
    ...toProductCardData(productCard, position),
    inWishlist,
  }

  const variant = isMultipleProductCard(productCard)
    ? activeShade
    : productCard.variants[0]

  const {
    brandName = "",
    productName = "",
    highlightedPrice = "primary",
  } = productCardData
  const locationTypeDetails = `${productName} ${brandName} ${
    activeShade?.shade ? activeShade?.shade : ""
  }`

  const addToBag = useCallback(
    async (locationType) => {
      if (isGiftMembership) {
        await addBasketItemsWithSpinner(
          [
            {
              quantity: 1,
              variantId: giftMembershipProductId,
              variantSku:
                locale === "en-US" ? "BP_PLUS_GIFT_US" : "BP_PLUS_GIFT_UK",
              type: "product",
              origin: "user",
            },
          ],
          locationType,
          locationTypeDetails
        )
          .then(() => navigateTo("/basket"))
          .catch(() => Logger.error(`Error adding membership to basket`))

        return
      }

      if (!variant) return

      const items: AddBasketItemType[] = [
        {
          quantity: 1,
          type: "product",
          origin: "user",
          variantSku: variant.sku,
        },
      ]

      await addBasketItemsWithSpinner(items, locationType, locationTypeDetails)
        .then(() => {
          setIsShadeSelectorOpen(false)
          const variantType = toVariantType(variant, productCard)
          setConfirmation?.(true)
          setConfirmationVariant?.(variantType)
        })
        .catch(() => Logger.error(`Error adding item to basket`))
    },
    [
      isGiftMembership,
      variant,
      locationTypeDetails,
      giftMembershipProductId,
      locale,
      productCard,
      setConfirmation,
      setConfirmationVariant,
    ]
  )

  const closeModal = () => {
    if (shadeButtonClicked) {
      track({
        event: "Page Element Clicked",
        properties: {
          module_content: "Close Shade Selector",
          location_type: "PLP",
          module_content_details: `Clicked ${locationTypeDetails}`,
        },
      })
    } else {
      track({
        event: "Page Element Clicked",
        properties: {
          module_content: "Close Shade Selector",
          location_type: "PLP",
        },
      })
    }
    setIsShadeSelectorOpen(false)
    setShadeButtonClicked(false)
  }

  const trackShadeClick = () => {
    setShadeButtonClicked(true)
  }

  const trackShadeClicked = () => {
    track({
      event: "Product Clicked",
      properties: {
        location_type: "PLP Shade Selector",
        location_type_details: "View More Details Button",
        sku: activeShade?.sku || "",
        name: productName,
        brand: brandName,
        variant: activeShade?.shade || "",
        members_price: activeShade?.pricing.primary.raw || 0,
        typical_price: activeShade?.pricing.secondary.raw || 0,
        url: activeShade?.href || "",
        currency: activeShade?.pricing.primary.currency || "",
        result_id: resultId ?? "", //empty for current plp, result id for constructor browse
      },
    })
  }

  const trackProductClicked = () => {
    if (!variant) {
      return
    }

    const resultTypes: ResultType[] = [
      "browse",
      "recommendation",
      "search",
      "suggestions",
    ]
    const isResultTypeValid = resultType && resultTypes.includes(resultType)

    const currency =
      variant.pricing.primary.currency || variant.pricing.primary.currencySymbol

    const productClickedEvent: SegmentTrackProductClickedEvent = {
      event: "Product Clicked",
      properties: {
        sku: variant.sku,
        name: productName,
        brand: brandName,
        variant: variant.shade || productCard.title,
        members_price: variant.pricing.primary.raw,
        typical_price: variant.pricing.secondary.raw,
        url: variant.href,
        currency,
        result_id: resultId ?? "", //empty for current plp, add result id for constructor browse
        position: cardPosition,
        resultCount,
        numResultsPerPage,
        ...(isResultTypeValid && { result_type: resultType }),
      },
    }

    track(productClickedEvent)
  }

  const multivariantButtonText = `shop for ${activeShade?.pricing?.[highlightedPrice]?.formatted}`
  return (
    <>
      {isMultipleProductCard(productCard) && isShadeSelectorOpen && (
        <ShadeSelectorModal
          isModalOpen={isShadeSelectorOpen}
          onCloseModal={closeModal}
          styleVariant="disableScroll"
          drawerHeight="fullHeight"
        >
          <ShadeSelectorHeader
            onCloseModal={closeModal}
            styleVariant="stickyHeader"
          >
            Select Shade
          </ShadeSelectorHeader>
          <ShadeSelectorModalContentPLP
            setActiveShade={setActiveShade}
            activeShade={activeShade}
            styleVariant="scrollShadow"
            product={productCard}
            onButtonClick={trackShadeClick}
          />
          <ShadeSelectorModalFooter
            onCloseModal={closeModal}
            styleVariant="stickyFooter"
            showCancelButton={false}
          >
            <Button
              ariaLabel="Add to basket"
              variant="primary"
              disabled={!activeShade?.availability.inStock}
              handleClick={async () => {
                if (activeShade) {
                  const activeVariant = toVariantType(activeShade, productCard)

                  if (productCardData.swatchImages?.length) {
                    setIsShadeSelectorOpen(false)
                    await addToBag("Shade Selector Modal")
                  }
                  setConfirmationVariant?.(activeVariant)
                }
              }}
              fullWidth
            >
              {multivariantButtonText}
            </Button>

            <a
              href={activeShade?.href}
              className={link}
              onClick={trackShadeClicked}
            >
              <span>View more details</span>
            </a>
          </ShadeSelectorModalFooter>
        </ShadeSelectorModal>
      )}

      <ProductCard
        product={productCardData}
        productCard={productCard}
        addToBasket={() => {
          const location =
            resultType === "search" ? "Search PLP page" : "PLP Page"
          return addToBag(location)
        }}
        setIsShadeSelectorOpen={setIsShadeSelectorOpen}
        setConfirmation={setConfirmation}
        confirmationVariant={confirmationVariant}
        setConfirmationVariant={setConfirmationVariant}
        onClick={trackProductClicked}
      >
        <ProductCardWishlist
          onToggle={() => {
            if (!isLoggedIn) {
              return navigate("/login")
            }
            const productVariant = productCard.variants[0]
            if (productCardData?.inWishlist) {
              return removeWishlistItemBySKU(productVariant.sku)
            }
            addWishlistItemBySKU(productVariant.sku)
          }}
        >
          <ProductCardBadge />
          <ProductCardRibbon />
          <ProductCardImage useCloudinary={useCloudinary} />
        </ProductCardWishlist>
        <ProductCardRating />
        <ProductCardBrandName />
        <ProductCardProductName />
        <ProductCardVariationName />
        <ProductCardColorSwatch />
        <div className={productCardFooter}>
          <ProductCardButton />
          <SecondaryProductCardButton
            handleClick={handleOnSecondaryButtonClicked}
            productCard={productCard}
          />
        </div>
      </ProductCard>
    </>
  )
}
